import { classNames } from '@progress/kendo-react-common'
import MoneyInput from 'components/MoneyInput'
import { useAppContext } from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { SubmitHandler, useForm } from 'react-hook-form'
import { CognitoErrorTypes } from 'types/cognito'
import { GeneralFormValues } from './types'
import { putApiRequest, useFetch } from 'utils/fetch-helper'
import { FetchMethods } from 'utils/types'
import { UserModel } from '../types'
import { User } from 'types/user'
import { useTenantContext } from 'context/TenantContext'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { StaffUser } from 'types/staff'

export function Pay({
    onCloseClick,
    onSubmitClick,
}: {
    onCloseClick(): void
    onSubmitClick(): void
}) {
    const { setGlobalError } = useAppErrorApiContext()
    const {
        setGlobalLoading,
        translations,
    } = useAppContext()
    const { StaffModal, user: userFromContext } = useAuth()
    const { tenant } = useTenantContext()
    const { attributes, setAttributes } = StaffModal

    const { data: data_business } = useFetch(
        `/v1/tenants/get-by-user-id?userId=${userFromContext?.id}`,
        FetchMethods.GET,
        !!userFromContext?.id
    )

    const {
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<GeneralFormValues>({
        mode: 'onSubmit',
    })

    const onSubmit: SubmitHandler<GeneralFormValues> = async () => {
        const { email, phone, first_name, last_name } = attributes as User
        const staff_image = attributes?.image_url as string
        const defaultData =
            data_business[0]?.user?.id === attributes?.user_id
                ? { ...data_business[0].user }
                : {}
        const staff_user = attributes?.user as StaffUser
        const user: UserModel = {
            ...defaultData,
            cognitoId: staff_user?.cognito_id,
            id: attributes?.user_id,
            firstName: first_name,
            lastName: last_name,
            email: email || staff_user?.email,
            imageUrl: staff_image || staff_user?.image_url,
            phone,
        }

        const staff_dto = {
            cognitoId: attributes?.cognito_id as string,
            id: attributes?.id as string,
            tenant,
            hourlyWage: attributes?.hourly_rate as number,
            monthlyWage: attributes?.monthly_rate as number,
            overtimeRate: attributes?.overtime_rate as number,
            user,
            notes: attributes?.notes,
        }

        setGlobalLoading(true)
        await putApiRequest(`/v1/staff/${attributes?.id}`, staff_dto)
            .then((results) => {
                if (results.status === 400) {
                    throw new Error(results.message)
                } else {
                    reset()
                    onSubmitClick()
                }
            })
            .catch((e) => {
                setGlobalError(e)
            })
        setGlobalLoading(false)
    }

    return (
        <form method="POST" onSubmit={handleSubmit(onSubmit)}>
            {/* Pay Fields */}
            <div className="pb-6 lg:flex gap-5">
                <div className="lg:pb-0 lg:w-full flex flex-col gap-2">
                    <fieldset className="pb-5 aot-form-group">
                        <label
                            htmlFor="hourly-rate"
                            className={classNames(
                                'block text-sm',
                                errors.hourly_rate?.type
                                    ? 'text-red-700'
                                    : ''
                            )}
                        >
                            {translations['hourly_rate']}
                        </label>

                        <MoneyInput
                            id="hourly-rate"
                            className={classNames(
                                'px-5 py-1 mt-1 block w-full rounded-md focus:ring-0 focus:border-transparent',
                                errors.hourly_rate?.type
                                    ? 'border-red-300 bg-red-100'
                                    : ''
                            )}
                            defaultValue={(attributes?.hourly_rate as number) || 0}
                            onMoneyChange={(v: string) => {
                                setAttributes({
                                    ...attributes,
                                    hourly_rate: v,
                                })
                            }}
                            required={true}
                        />

                        {errors.hourly_rate?.type === 'required' && (
                            <span className="text-sm text-red-700">
                                {translations['hourly_rate_required']}
                            </span>
                        )}
                    </fieldset>

                    <fieldset className="pb-5 aot-form-group">
                        <label
                            htmlFor="monthly-rate"
                            className={classNames(
                                'block text-sm',
                                errors.monthly_rate?.type
                                    ? 'text-red-700'
                                    : ''
                            )}
                        >
                            {translations['monthly_rate']}
                        </label>
                        <MoneyInput
                            id="monthly-rate"
                            className={classNames(
                                'px-5 py-1 mt-1 focus:ring-0 focus:border-transparent block w-full rounded-md',
                                errors.monthly_rate?.type
                                    ? 'border-red-300 bg-red-100'
                                    : ''
                            )}
                            defaultValue={
                                (attributes?.monthly_rate as number) || 0
                            }
                            onMoneyChange={(v: string) => {
                                setAttributes({
                                    ...attributes,
                                    monthly_rate: v,
                                })
                            }}
                            required={true}
                        />
                        {errors.monthly_rate?.type === 'required' && (
                            <span className="text-sm text-red-700">
                                {translations['monthly_rate_required']}
                            </span>
                        )}
                    </fieldset>

                    <fieldset className="pb-5 aot-form-group">
                        <label
                            htmlFor="overtime-rate"
                            className={classNames(
                                'block text-sm',
                                errors.overtime_rate?.type
                                    ? 'text-red-700'
                                    : ''
                            )}
                        >
                            {translations['overtime_rate']}
                        </label>
                        <MoneyInput
                            id="overtime-rate"
                            className={classNames(
                                'px-5 py-1 mt-1 focus:ring-0 focus:border-transparent block w-full rounded-md',
                                errors.overtime_rate?.type
                                    ? 'border-red-300 bg-red-100'
                                    : ''
                            )}
                            defaultValue={
                                (attributes?.overtime_rate as number) || 0
                            }
                            onMoneyChange={(v: string) => {
                                setAttributes({
                                    ...attributes,
                                    overtime_rate: v,
                                })
                            }}
                            required={true}
                        />
                        {errors.overtime_rate?.type === 'required' && (
                            <span className="text-sm text-red-700">
                                {translations['overtime_rate_required']}
                            </span>
                        )}
                    </fieldset>
                </div>
            </div>

            {/* Buttons */}
            <div>
                {Object.values(CognitoErrorTypes).includes(
                    errors.email?.type as CognitoErrorTypes
                ) ? (
                    <div className="bg-red-100 rounded px-3 py-1 mb-5">
                        {errors.email?.message && (
                            <span className="text-sm text-red-700">
                                {errors.email?.message}
                            </span>
                        )}
                    </div>
                ) : (
                    ''
                )}

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        className="border border-gray-300 rounded-lg inline-block btn-gap"
                        onClick={onCloseClick}
                    >
                        {translations['cancel']}
                    </button>
                    <button
                        type="submit"
                        className={classNames(
                            'group relative flex justify-center',
                            'btn-gap border border-transparent',
                            'rounded-md text-white',
                            'focus:outline-none',
                            !attributes?.id
                                ? 'bg-primary-light'
                                : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                        )}
                        disabled={!attributes?.id}
                    >
                        {translations['save']}
                    </button>
                </div>
            </div>
        </form>
    )
}

export default Pay
